import React from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';

import { rem } from '../../../utilities/style';
import { containerStyles } from '../../../styles/layout';
import { colors } from '../../../theme/colors';
import { media } from '../../../theme/media';

import PricingCard from './PricingCard';
import PricingCardFullWidth from './PricingCardFullWidth';

const Container = styled.div`
  background-color: ${({ backgroundColor }) => (
    backgroundColor === 'White' ? colors.white : colors.blue['50']
  )};
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: ${rem(40)} 0 ${rem(40)} 0;
  ${containerStyles.regular}
`;

const PricingTableContainer = styled.div`
  display: grid;
  gap: ${rem(40)};

  ${media('>small')} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media('>smedium')} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PricingTable = ({ pricingCards, backgroundColor }) => {
  // pull out all pricingcards without full width
  const columnCards = pricingCards.filter((pricingCard) => !pricingCard.fullWidth);
  const fullWidthCards = pricingCards.filter((pricingCard) => pricingCard.fullWidth);

  return (
    <Container
      backgroundColor={backgroundColor}
    >
      <ContentWrapper>
        <PricingTableContainer>
          {columnCards && columnCards.map((pricingCard, index) => (
            <PricingCard
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              title={pricingCard.title}
              price={pricingCard.price}
              durationText={pricingCard.durationText}
              pricingNote={pricingCard.pricingNote}
              originalPriceText={pricingCard.originalPriceText}
              featureBadgeText={pricingCard.featureBadgeText}
              ctaText={pricingCard.ctaText}
              ctaLink={pricingCard.ctaLink}
              secondaryCtaText={pricingCard.secondaryCtaText}
              secondaryCtaLink={pricingCard.secondaryCtaLink}
              description={pricingCard.description}
              outlined={backgroundColor === 'White'}
            />
          ))}
          {fullWidthCards && fullWidthCards.map((pricingCard, index) => (
            <PricingCardFullWidth
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              title={pricingCard.title}
              price={pricingCard.price}
              durationText={pricingCard.durationText}
              pricingNote={pricingCard.pricingNote}
              originalPriceText={pricingCard.originalPriceText}
              featureBadgeText={pricingCard.featureBadgeText}
              ctaText={pricingCard.ctaText}
              ctaLink={pricingCard.ctaLink}
              secondaryCtaText={pricingCard.secondaryCtaText}
              secondaryCtaLink={pricingCard.secondaryCtaLink}
              description={pricingCard.description}
              borderColor={backgroundColor === 'White' ? colors.gray['200'] : colors.blue['200']}
            />
          ))}
        </PricingTableContainer>
      </ContentWrapper>
    </Container>
  );
};

PricingTable.propTypes = {
  pricingCards: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    price: PropTypes.string,
    durationText: PropTypes.string,
    originalPriceText: PropTypes.string,
    pricingNote: PropTypes.string,
    buttonText: PropTypes.string,
    buttonLink: PropTypes.string,
    secondaryButtonText: PropTypes.string,
    secondaryButtonLink: PropTypes.string,
    description: PropTypes.shape({
      raw: PropTypes.string,
    }),
    fullWidth: PropTypes.bool
  })),
  backgroundColor: PropTypes.oneOf(['White', 'Light Blue']),
};

export const query = graphql`
  fragment PricingTableFragment on ContentfulPricingTable {
    id
    __typename
    pricingCards {
      title,
      price,
      durationText,
      pricingNote,
      originalPriceText,
      featureBadgeText,
      ctaText,
      ctaLink,
      secondaryCtaText,
      secondaryCtaLink,
      description {
        raw
      },
      fullWidth
    }
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default PricingTable;
